import { useCallback, useEffect, useMemo, useState } from 'react'
import type { DonneesPlateforme, Source } from '../api/types'
import { Revenus, Repartition } from './Graphiques'
import { BoitiersGps } from './BoitiersGps'
import { ClientsApi } from './ClientsApi'
import { FicheOrg } from './FicheOrg'
import { Icone, Logo } from './Icone'
import { Kpis } from './Kpis'
import { ModaleAbonnement } from './ModaleAbonnement'
import { ModaleClient } from './ModaleClient'
import { useNotification } from './Notification'
import { TableOrgs } from './TableOrgs'

type Etat =
  | { statut: 'chargement' }
  | { statut: 'erreur'; message: string }
  | { statut: 'pret'; donnees: DonneesPlateforme }

/** La console après connexion : indicateurs, graphiques, tableau des organisations. */
export function Console({
  source,
  email,
  surDeconnexion,
}: {
  source: Source
  email: string
  surDeconnexion: () => void
}) {
  const [etat, setEtat] = useState<Etat>({ statut: 'chargement' })
  const [fiche, setFiche] = useState<string | null>(null)
  const [edition, setEdition] = useState(false)
  const [nouveau, setNouveau] = useState(false)
  const [versionJournal, setVersionJournal] = useState(0)
  const [ecran, setEcran] = useState<'ensemble' | 'boitiers'>('ensemble')
  const { notifier, bandeau } = useNotification()

  /** `silencieux` : garde l'écran en place pendant le rechargement (après une écriture). */
  const charger = useCallback(async (silencieux: boolean) => {
    if (!silencieux) setEtat({ statut: 'chargement' })
    try {
      const donnees = await source.charger()
      setEtat({ statut: 'pret', donnees })
      return true
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      if (silencieux) notifier(`Rechargement impossible : ${message}`)
      else setEtat({ statut: 'erreur', message })
      return false
    }
  }, [source, notifier])

  useEffect(() => { void charger(false) }, [charger])

  const donnees = etat.statut === 'pret' ? etat.donnees : null
  const orgFiche = useMemo(
    () => donnees?.organisations.find((o) => o.org_id === fiche) ?? null,
    [donnees, fiche],
  )

  return (
    <div className="appli">
      <header className="entete">
        <span className="logo-carre"><Logo /></span>
        <div>
          <div className="entete-nom">Swift<span>Deliver</span></div>
          <div className="entete-sous">Supervision plateforme</div>
        </div>
        <span className="esp" />
        <span className="pilule-user" title={email}><span className="point" />{email}</span>
        <button type="button" className="btn btn-entete btn-sm" onClick={surDeconnexion}>
          <Icone nom="logout" /> Se déconnecter
        </button>
      </header>

      <nav className="onglets" aria-label="Écrans de la supervision">
        <button type="button" aria-current={ecran === 'ensemble' ? 'page' : undefined} onClick={() => setEcran('ensemble')}>Vue d'ensemble</button>
        <button type="button" aria-current={ecran === 'boitiers' ? 'page' : undefined} onClick={() => setEcran('boitiers')}>Boîtiers GPS</button>
      </nav>

      <main className="page">
        {ecran === 'boitiers' && <BoitiersGps source={source} notifier={notifier} />}

        {ecran === 'ensemble' && <>
        <div className="tete-page">
          <h1>Vue d'ensemble</h1>
          <p>Tous les tenants, leurs paliers et le revenu de la plateforme</p>
        </div>

        {etat.statut === 'chargement' && (
          <div className="kpis" aria-busy="true" aria-label="Chargement">
            {Array.from({ length: 6 }, (_, i) => (
              <div className="carte kpi" key={i}><div className="sk" /><div className="sk court" /></div>
            ))}
          </div>
        )}

        {etat.statut === 'erreur' && (
          <div className="carte vide" role="alert">
            <Icone nom="alert" taille={22} />
            <p>Chargement impossible : {etat.message}</p>
            <button type="button" className="btn btn-plein btn-sm" onClick={() => void charger(false)}>
              <Icone nom="refresh" /> Réessayer
            </button>
          </div>
        )}

        {donnees && (
          <>
            {donnees.avertissements.map((a) => (
              <p className="encart encart-alerte" role="status" key={a}><Icone nom="alert" /> {a}</p>
            ))}
            <Kpis donnees={donnees} />
            <div className="grille">
              <Revenus revenus={donnees.revenus} />
              <Repartition donnees={donnees} />
            </div>
            <TableOrgs
              donnees={donnees}
              surOuvrir={setFiche}
              surNouveau={() => setNouveau(true)}
              surExport={() => notifier('Export téléchargé')}
            />
            {/* Clients API sans flotte (0048) : clients opérés de l'opératrice, validés ici. */}
            <ClientsApi
              source={source}
              operatorOrgId={donnees.organisations.find((o) => o.is_operator)?.org_id ?? null}
              notifier={notifier}
            />
          </>
        )}
        </>}
      </main>

      {donnees && orgFiche && (
        <FicheOrg
          org={orgFiche}
          plans={donnees.plans}
          maintenant={donnees.chargeLe}
          source={source}
          versionJournal={versionJournal}
          surModifier={() => setEdition(true)}
          surFermer={() => { setEdition(false); setFiche(null) }}
          surAccesApiChange={async () => { await charger(true) }}
          notifier={notifier}
        />
      )}

      {donnees && orgFiche && edition && !orgFiche.is_operator && (
        <ModaleAbonnement
          org={orgFiche}
          plans={donnees.plans}
          maintenant={donnees.chargeLe}
          source={source}
          surFermer={() => setEdition(false)}
          surEnregistre={async () => {
            setVersionJournal((v) => v + 1)
            notifier(`Abonnement de « ${orgFiche.brand_name || orgFiche.name} » mis à jour`)
            await charger(true)
          }}
        />
      )}

      {donnees && nouveau && (
        <ModaleClient
          plans={donnees.plans}
          source={source}
          surFermer={() => setNouveau(false)}
          surCree={() => { void charger(true) }}
          notifier={notifier}
        />
      )}

      {bandeau}
    </div>
  )
}
